import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  AlertTriangle,
  Check,
  Loader2,
  Trash2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BankTransaction } from '@/hooks/useBankTransactions';
import { SearchableReceivable } from '@/hooks/useSearchReceivables';
import { usePayablePaymentCharges } from '@/hooks/usePayablePaymentCharges';
import { useReceivablePaymentCharges } from '@/hooks/useReceivablePaymentCharges';
import { cn } from '@/lib/utils';

export interface ReceivableAdjustment {
  id: string;
  type: 'receivable' | 'payable';
  /** true = título baixado (vínculo puro, R3); false = aberto (baixa+vínculo) */
  settled: boolean;
  description: string;
  original_value: number;
  interest_value: number;
  penalty_value: number;
  discount_value: number;
  fee_value: number;
  partial_payment: number;
  final_value: number;
}

interface ReviewValuesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: BankTransaction | null;
  receivables: SearchableReceivable[];
  onConfirm: (adjustments: ReceivableAdjustment[]) => void;
  onBack: () => void;
  isLoading?: boolean;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const parseCurrencyInput = (value: string): number => {
  const cleaned = value.replace(/[^\d,.-]/g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
};

export const ReviewValuesDialog = ({
  open,
  onOpenChange,
  transaction,
  receivables,
  onConfirm,
  onBack,
  isLoading = false,
}: ReviewValuesDialogProps) => {
  const [adjustments, setAdjustments] = useState<ReceivableAdjustment[]>([]);

  // D8c: encargos reais dos payments (mesma queryKey do Buscar = cache)
  const receivableIds = receivables.filter((r) => r.type === 'receivable').map((r) => r.id);
  const payableIds = receivables.filter((r) => r.type === 'payable').map((r) => r.id);
  const { data: recvCharges } = useReceivablePaymentCharges(receivableIds);
  const { data: payCharges } = usePayablePaymentCharges(payableIds);

  // Recalcular valor final quando ajustes mudam
  const calculateFinalValue = (adj: ReceivableAdjustment): number => {
    return adj.original_value + adj.interest_value + adj.penalty_value - adj.discount_value - adj.fee_value - adj.partial_payment;
  };

  // Inicializar adjustments por cenário (D8c): baixado = principal + encargos
  // pré-preenchidos da decomposição real; aberto = saldo + campos zerados
  useEffect(() => {
    if (receivables.length > 0) {
      setAdjustments(
        receivables.map(r => {
          const remaining = r.total_value - (r.paid_value || 0);
          const settled = r.status === 'paid' || remaining <= 0.009;
          const charges = r.type === 'receivable' ? recvCharges?.[r.id] : payCharges?.[r.id];
          const adj: ReceivableAdjustment = {
            id: r.id,
            type: r.type,
            settled,
            description: r.description,
            original_value: settled ? (charges?.totalPrincipal ?? r.paid_value ?? 0) : remaining,
            interest_value: settled ? (charges?.totalInterest ?? 0) : 0,
            penalty_value: settled ? (charges?.totalFine ?? 0) : 0,
            discount_value: settled ? (charges?.totalDiscount ?? 0) : 0,
            fee_value: settled ? (charges?.totalFee ?? 0) : 0,
            partial_payment: 0,
            final_value: 0,
          };
          adj.final_value = calculateFinalValue(adj);
          return adj;
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivables, recvCharges, payCharges]);

  const updateAdjustment = (id: string, field: keyof ReceivableAdjustment, value: number) => {
    setAdjustments(prev =>
      prev.map(adj => {
        if (adj.id !== id) return adj;
        const updated = { ...adj, [field]: value };
        updated.final_value = calculateFinalValue(updated);
        return updated;
      })
    );
  };

  const removeAdjustment = (id: string) => {
    setAdjustments(prev => prev.filter(adj => adj.id !== id));
  };

  // Calcular totais
  const bankValue = Math.abs(transaction?.value || 0);
  const totalSystem = useMemo(() => 
    adjustments.reduce((sum, adj) => sum + adj.final_value, 0), 
    [adjustments]
  );
  const difference = bankValue - totalSystem;
  const canReconcile = Math.abs(difference) < 0.01;

  const handleConfirm = () => {
    if (!canReconcile || adjustments.length === 0) return;
    onConfirm(adjustments);
  };

  if (!transaction) return null;

  const isCredit = transaction.transaction_type === 'credit';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-4">
          <DialogTitle>Revisar Valores</DialogTitle>
        </DialogHeader>

        {/* Card da transação bancária */}
        <div className={cn(
          'mx-6 p-4 rounded-lg border',
          isCredit ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'
        )}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                'p-2 rounded-full',
                isCredit ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
              )}>
                {isCredit ? <ArrowDownCircle className="h-5 w-5" /> : <ArrowUpCircle className="h-5 w-5" />}
              </div>
              <div>
                <p className="font-medium">{transaction.description}</p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(`${transaction.transaction_date}T12:00:00`), 'dd/MM/yyyy', { locale: ptBR })}
                </p>
              </div>
            </div>
            <p className={cn('text-xl font-bold', isCredit ? 'text-green-600' : 'text-red-600')}>
              {formatCurrency(bankValue)}
            </p>
          </div>
        </div>

        {/* Alerta de diferença */}
        {!canReconcile && (
          <Alert variant="destructive" className="mx-6 border-amber-500/50 bg-amber-500/10">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-700">
              Corrija a diferença de {formatCurrency(difference)}. O valor está diferente do banco.
            </AlertDescription>
          </Alert>
        )}

        {/* Tabela de ajustes */}
        <ScrollArea className="flex-1 px-6">
          <div className="space-y-4 py-4">
            {adjustments.map((adj, index) => (
              <div key={adj.id} className="p-4 border rounded-lg bg-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      'p-1 rounded-full',
                      adj.type === 'receivable' ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
                    )}>
                      {adj.type === 'receivable' ? <ArrowDownCircle className="h-4 w-4" /> : <ArrowUpCircle className="h-4 w-4" />}
                    </div>
                    <div>
                      <p className="font-medium">{adj.description}</p>
                      <p className="text-sm text-muted-foreground">
                        {receivables.find(r => r.id === adj.id)?.customer_name || receivables.find(r => r.id === adj.id)?.supplier_name}
                      </p>
                    </div>
                  </div>
                  {adjustments.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAdjustment(adj.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Valor Lançado</Label>
                    <Input
                      value={formatCurrency(adj.original_value)}
                      disabled
                      className="bg-muted text-right"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-green-600">+ Juros</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={adj.interest_value || ''}
                      onChange={(e) => updateAdjustment(adj.id, 'interest_value', parseCurrencyInput(e.target.value))}
                      placeholder="0,00"
                      className="text-right"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-green-600">+ Multa</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={adj.penalty_value || ''}
                      onChange={(e) => updateAdjustment(adj.id, 'penalty_value', parseCurrencyInput(e.target.value))}
                      placeholder="0,00"
                      className="text-right"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-red-600">- Desconto</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={adj.discount_value || ''}
                      onChange={(e) => updateAdjustment(adj.id, 'discount_value', parseCurrencyInput(e.target.value))}
                      placeholder="0,00"
                      className="text-right"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-red-600">- Tarifa</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={adj.fee_value || ''}
                      onChange={(e) => updateAdjustment(adj.id, 'fee_value', parseCurrencyInput(e.target.value))}
                      placeholder="0,00"
                      className="text-right"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Valor Final</Label>
                    <Input
                      value={formatCurrency(adj.final_value)}
                      disabled
                      className={cn(
                        'text-right font-bold',
                        adj.type === 'receivable' ? 'text-green-600' : 'text-red-600'
                      )}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        {/* Footer com totais */}
        <div className="border-t p-4 bg-muted/30">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <div>
                <span className="text-muted-foreground">Valor do banco:</span>
                <span className="ml-2 font-bold">{formatCurrency(bankValue)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Total no sistema:</span>
                <span className="ml-2 font-bold">{formatCurrency(totalSystem)}</span>
              </div>
              <div className={cn(
                'flex items-center gap-1',
                canReconcile ? 'text-green-600' : 'text-amber-600'
              )}>
                {canReconcile ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <AlertTriangle className="h-4 w-4" />
                )}
                <span className="text-muted-foreground">Diferença:</span>
                <span className="font-bold">
                  {difference > 0 ? '+' : ''}{formatCurrency(difference)}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={onBack} disabled={isLoading}>
                Voltar
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={!canReconcile || adjustments.length === 0 || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Conciliando...
                  </>
                ) : (
                  'Conciliar'
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
